import i18n from '@/shared/i18n';

export const DEFAULT_APPOINTMENT_DURATION = 30;
export const MIN_APPOINTMENT_DURATION = 15;

export interface AppointmentDurationOption {
  value: number;
  labelKey: string;
}

/** Suggested durations when no treatment is linked (free entry still allowed). */
export const APPOINTMENT_DURATION_OPTIONS: AppointmentDurationOption[] = [
  { value: 15, labelKey: 'appointment:durations.min15' },
  { value: 30, labelKey: 'appointment:durations.min30' },
  { value: 45, labelKey: 'appointment:durations.min45' },
  { value: 60, labelKey: 'appointment:durations.hour1' },
  { value: 90, labelKey: 'appointment:durations.hour1Half' },
  { value: 120, labelKey: 'appointment:durations.hour2' },
];

/** Preset default durations for appointment type catalog. */
export const APPOINTMENT_TYPE_DEFAULT_DURATIONS = [15, 30, 45, 60, 75, 90, 120] as const;

export const parseAppointmentDurationInput = (
  raw: string | number | null | undefined,
  fallback: number | null = DEFAULT_APPOINTMENT_DURATION
): number | null => {
  if (raw == null || raw === '') return fallback;
  const next = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(next) ? next : fallback;
};

/** Populated appointment type from API, or legacy string. */
export type AppointmentTypeRef =
  | string
  | {
      _id?: string;
      id?: string;
      name?: string;
      code?: string;
      color?: string;
      defaultDuration?: number;
    }
  | null
  | undefined;

const OBJECT_ID_RE = /^[a-f\d]{24}$/i;

export const getAppointmentTypeId = (value: AppointmentTypeRef): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  return String(value.id || value._id || '');
};

export const getAppointmentTypeLabel = (value: AppointmentTypeRef): string => {
  if (value == null || value === '') {
    return i18n.t('appointment:fallbacks.notAvailable');
  }
  if (typeof value === 'object') {
    if (value.name?.trim()) return value.name.trim();
    return i18n.t('appointment:fallbacks.notAvailable');
  }
  // Raw ObjectId must never be shown as a label (unpopulated ref).
  if (OBJECT_ID_RE.test(value)) {
    return i18n.t('appointment:fallbacks.notAvailable');
  }
  // Legacy English enum string stored before ObjectId migration
  return value;
};
